import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { Campaign } from '../../services/campaign.service';
import { ConfirmActionModalComponent } from '../../shared/components/confirm-action-modal';

/**
 * Modal component that prompts the user to confirm archiving a campaign.
 * @component
 * @selector app-archive-campaign-modal
 * @example
 *   <app-archive-campaign-modal
 *     [campaign]="sampleCampaign"
 *     (confirmArchive)="onArchiveConfirmed($event)"
 *     (close)="onModalClosed()">
 *   </app-archive-campaign-modal>
 * @input Campaign | null campaign - The campaign object to display details for and potentially archive; null when no campaign is selected.
 * @output EventEmitter<void> close - Emitted when the modal is closed or cancelled (no archive action taken).
 * @output EventEmitter<Campaign> confirmArchive - Emitted with the Campaign payload when the user confirms the archive action.
 *
 * Key methods:
 * - onClose(): Emits the close event to notify parent components to close the modal.
 * - onConfirmArchive(): If a campaign is present, emits confirmArchive with the campaign to trigger archiving in the parent.
 *
 * Lifecycle hooks:
 * - This component does not implement any additional Angular lifecycle hooks (e.g., ngOnInit) — it is a simple, state-driven modal component.
 */
@Component({
  selector: 'app-archive-campaign-modal',
  standalone: true,
  imports: [ConfirmActionModalComponent],
  templateUrl: './archive-campaign-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./archive-campaign-modal.component.scss'],
})
export class ArchiveCampaignModalComponent {
  @Input() campaign: Campaign | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() confirmArchive = new EventEmitter<Campaign>();

  onClose(): void {
    this.close.emit();
  }

  onConfirmArchive(): void {
    if (this.campaign) {
      this.confirmArchive.emit(this.campaign);
    }
  }
}
